/**
 * ControlCardContainer
 * 
 * Dependency injection container for control card services.
 * Provides singleton instances and manages dependencies.
 */

import { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '../../../../lib/supabase/client'
import { GenerateControlCardUseCase } from '../use-cases/GenerateControlCardUseCase'
import { ControlCardEquipmentRepository } from '../../infrastructure'
import { ProfessionalControlCardTemplate } from '../../presentation'

/**
 * Dependency injection container for control card system
 */
export class ControlCardContainer {
  private static instance: ControlCardContainer | null = null
  private useCaseInstance: GenerateControlCardUseCase | null = null
  private supabaseClient: SupabaseClient

  private constructor(supabaseClient?: SupabaseClient) {
    this.supabaseClient = supabaseClient || supabase
  }

  /**
   * Get singleton instance of the container
   */
  static getInstance(supabaseClient?: SupabaseClient): ControlCardContainer {
    if (!this.instance) {
      this.instance = new ControlCardContainer(supabaseClient)
    }
    return this.instance
  }

  /**
   * Reset singleton instance (useful for testing)
   */
  static resetInstance(): void {
    this.instance = null
  }

  /**
   * Get GenerateControlCardUseCase instance
   */
  getGenerateControlCardUseCase(): GenerateControlCardUseCase {
    if (!this.useCaseInstance) {
      this.useCaseInstance = new GenerateControlCardUseCase(this.supabaseClient)
    }
    return this.useCaseInstance
  }

  /**
   * Create a new repository instance
   */
  createRepository(): ControlCardEquipmentRepository {
    return new ControlCardEquipmentRepository(this.supabaseClient)
  }

  /**
   * Create a new template instance
   */
  createTemplate(): ProfessionalControlCardTemplate {
    return new ProfessionalControlCardTemplate()
  }

  /**
   * Clear all cached instances (useful for testing)
   */
  clearCache(): void {
    this.useCaseInstance = null
  }
}

/**
 * Get default container instance
 */
export function getControlCardContainer(): ControlCardContainer {
  return ControlCardContainer.getInstance()
}

/**
 * Get use case from default container
 */
export function getGenerateControlCardUseCase(): GenerateControlCardUseCase {
  return getControlCardContainer().getGenerateControlCardUseCase()
}
